import React from 'react';
import LanguageIcon from '@material-ui/icons/Language';
import LayersIcon from '@material-ui/icons/Layers';
import NotesIcon from '../../icons/NotesIcon';
import BookIcon from '../../icons/BookIcon';
import { DocsSectionItem, DocsTextItem, DocsTocGroup } from '../types';
import { ROUTES } from './routes';

export const HOME_INTRO_TEXT =
  'Welcome to the Digital Realty DX Gateway documentation portal. This comprehensive resource provides everything you need to integrate with and leverage our API ecosystem.';

export const QUICK_START_ITEMS: DocsTextItem[] = [
  {
    title: 'Getting Started Guide',
    description: 'Your first steps with the DX Gateway',
  },
  {
    title: 'Registration',
    description: 'Create your account and get API access',
  },
];

export const DOCUMENTATION_SECTIONS: DocsSectionItem[] = [
  {
    icon: <BookIcon />,
    title: 'Landing Page Guide',
    description:
      'Complete guide to using the DX Portal, including account setup, API exploration, and best practices.',
  },
  {
    icon: <LanguageIcon />,
    title: 'Environments',
    description:
      'Information about our production, and staging environments.',
  },
  {
    icon: <LayersIcon />,
    title: 'Service Management API',
    description:
      'Manage data center operations, including physical access requests and technical assistance scheduling.',
  },
  {
    icon: <NotesIcon />,
    title: 'Release Notes',
    description:
      'Latest updates and changes across all DX Gateway APIs and services.',
  },
];

export const KEY_FEATURES: DocsTextItem[] = [
  {
    title: 'Unified API Gateway',
    description: 'Single entry point for all Digital Realty APIs',
  },
  {
    title: 'OAuth 2.0 Authentication',
    description: 'Secure, industry-standard authentication',
  },
  {
    title: 'Rate Limiting & Throttling',
    description: 'Built-in traffic management',
  },
  {
    title: 'Comprehensive Monitoring',
    description: 'Full observability with tracing and metrics',
  },
  {
    title: 'Developer-Friendly',
    description: 'Extensive documentation and testing tools',
  },
];

export const SUPPORT_RESOURCES: DocsTextItem[] = [
  {
    title: 'Troubleshooting',
    description: 'Common issues and solutions',
  },
  {
    title: 'Support',
    description: 'Get help when you need it',
  },
  {
    title: "What's New",
    description: 'Latest updates and features',
  },
];

const SUPPORT_SLUG_BY_TITLE: Record<string, string> = {
  Troubleshooting: 'troubleshooting',
  Support: 'support',
  "What's New": 'whats-new',
};

export const SUPPORT_RESOURCES_ROUTES: Record<string, string> =
  Object.fromEntries(
    SUPPORT_RESOURCES.map(({ title }) => [
      title,
      `${ROUTES.landingPageGuide}/${SUPPORT_SLUG_BY_TITLE[title] ?? title}`,
    ]),
  );

export const GETTING_HELP_STEPS = [
  {
    textPrefix: 'Check our ',
    highlightedText: 'troubleshooting guide',
    textSuffix: '',
  },
  {
    textPrefix: 'Contact our ',
    highlightedText: 'support team',
    textSuffix: ' for personalized assistance',
  },
];

export const GETTING_HELP_CALLOUT = {
  textPrefix: 'Ready to get started? Begin with our ',
  highlightedText: 'Getting Started Guide',
  textSuffix: ' to create your first API integration.',
};

export const QUICK_START_ROUTES: Record<string, string | undefined> = {
  'Getting Started Guide': ROUTES.gettingStarted,
  Registration: ROUTES.registration,
};

export const DOC_SECTION_ROUTES: Record<string, string | undefined> = {
  'Landing Page Guide': ROUTES.landingPageGuide,
  Environments: ROUTES.environments,
  'Service Management API': ROUTES.serviceManagement,
  'Release Notes': ROUTES.releaseNotes,
};

export const HELP_STEP_ROUTES: Record<string, string | undefined> = {
  'Getting Started Guide': ROUTES.gettingStarted,
  'troubleshooting guide': ROUTES.troubleshooting,
  'support team': ROUTES.support,
};

export const CALLOUT_ROUTE = ROUTES.gettingStarted;

export const HOME_TABLE_OF_CONTENTS: DocsTocGroup[] = [
  {
    heading: 'Quick Start',
    route: ROUTES.gettingStarted,
    isActive: true,
    items: [],
  },
  {
    heading: 'Documentation Sections',
    items: [
      {
        label: 'Landing Page Guide',
        icon: <BookIcon />,
        route: ROUTES.landingPageGuide,
      },
      {
        label: 'Environments',
        icon: <LanguageIcon fontSize="inherit" />,
        route: ROUTES.environments,
      },
      {
        label: 'Release Notes',
        icon: <NotesIcon />,
        route: ROUTES.releaseNotes,
      },
    ],
  },
];
